import { useEffect, useState } from 'react';
import { adminApi, type SignupRequest } from '../api/organizations';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import type { TranslationKey } from '../i18n/translations';

type Status = 'pending' | 'approved' | 'rejected';
type Tab = 'requests' | 'organizations';

const ORG_ROLE_LABEL: Record<'org_admin' | 'maintenance' | 'user', TranslationKey> = {
  org_admin: 'adm_sign_role_org_admin',
  maintenance: 'adm_sign_role_maintenance',
  user: 'adm_sign_role_user',
};

const ORG_ROLE_BADGE: Record<'org_admin' | 'maintenance' | 'user', string> = {
  org_admin: 'bg-purple-100 text-purple-700',
  maintenance: 'bg-amber-100 text-amber-700',
  user: 'bg-gray-100 text-gray-700',
};

// Rôle plateforme → libellé traduit
const PLATFORM_ROLE_LABEL: Record<string, TranslationKey> = {
  super_admin: 'pg_role_super_admin',
  org_admin: 'adm_sign_role_org_admin',
  maintenance: 'adm_sign_role_maintenance',
  user: 'adm_sign_role_user',
};

export default function AdminSignupRequestsPage() {
  const { user } = useAuth();
  const { t, tp, locale } = useI18n();
  const [tab, setTab] = useState<Tab>('requests');
  const [status, setStatus] = useState<Status>('pending');
  const [list, setList] = useState<SignupRequest[]>([]);
  const [orgs, setOrgs] = useState<Awaited<ReturnType<typeof adminApi.listOrganizations>>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<{ id: number; name: string; memberCount: number } | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'requests') {
        setList(await adminApi.listSignupRequests(status));
      } else {
        setOrgs(await adminApi.listOrganizations());
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, tab]);

  if (user?.platformRole !== 'super_admin') {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto p-4 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
          {t('adm_sign_restricted')}
        </div>
      </div>
    );
  }

  const approve = async (r: SignupRequest) => {
    if (!window.confirm(t('adm_sign_confirm_approve', { org: r.organizationName, email: r.email }))) return;
    try {
      await adminApi.approveSignup(r.id);
      await refresh();
    } catch (e) {
      alert(t('adm_sign_error', { msg: (e as Error).message }));
    }
  };

  const reject = async (r: SignupRequest) => {
    const notes = window.prompt(t('adm_sign_prompt_reject', { email: r.email })) ?? undefined;
    try {
      await adminApi.rejectSignup(r.id, notes || undefined);
      await refresh();
    } catch (e) {
      alert(t('adm_sign_error', { msg: (e as Error).message }));
    }
  };

  const deleteOrg = (org: { id: number; name: string; members: unknown[] }) => {
    setPendingDelete({ id: org.id, name: org.name, memberCount: org.members.length });
  };

  const confirmDeleteOrg = async (password: string) => {
    if (!pendingDelete) return;
    await adminApi.deleteOrganization(pendingDelete.id, password);
    setPendingDelete(null);
    await refresh();
  };

  return (
    <div className="space-y-6">
      {/* ── Hero header ZCE ── */}
      <div className="relative overflow-hidden rounded-3xl shadow-2xl zce-fade-in">
        <div className="absolute inset-0 bg-gradient-to-br from-zce-navy-dark via-zce-navy to-zce-navy-light zce-gradient-anim" />
        <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-full bg-purple-500/20 blur-3xl zce-float-orb" />
        <div className="pointer-events-none absolute -bottom-32 -right-16 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl zce-float-orb" style={{ animationDelay: '-6s' }} />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="pointer-events-none absolute inset-0 zce-shimmer" />

        <div className="relative p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4 zce-fade-up">
              <div className="relative">
                <div className="absolute inset-0 bg-purple-400/40 rounded-xl blur-md zce-pulse-dot" />
                <div className="relative w-14 h-14 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.066z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white font-heading tracking-tight">
                  {t('adm_sign_title')}
                </h1>
                <p className="text-sm text-purple-200/80 mt-0.5">
                  {t('adm_sign_subtitle')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 zce-fade-up" style={{ animationDelay: '0.1s' }}>
              {tab === 'requests' && (
                <div className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-sm text-white">
                  <span className="text-purple-300 font-bold text-lg mr-1">{list.length}</span>
                  {tp('adm_sign_requests_word', list.length)}
                </div>
              )}
              {tab === 'organizations' && (
                <div className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-sm text-white">
                  <span className="text-purple-300 font-bold text-lg mr-1">{orgs.length}</span>
                  {tp('adm_sign_orgs_word', orgs.length)}
                </div>
              )}
            </div>
          </div>

          {/* ── Tabs inside hero ── */}
          <div className="flex gap-2 mt-5">
            {[
              { key: 'requests' as Tab, label: `📋 ${t('adm_sign_tab_requests')}`, icon: '' },
              { key: 'organizations' as Tab, label: `🏢 ${t('adm_sign_tab_orgs')}`, icon: '' },
            ].map((tb) => (
              <button
                key={tb.key}
                onClick={() => setTab(tb.key)}
                className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 transform hover:scale-105 active:scale-95 ${
                  tab === tb.key
                    ? 'bg-white/25 text-white border border-white/40 shadow-lg shadow-black/20 backdrop-blur-sm'
                    : 'text-white/60 hover:text-white hover:bg-white/10 border border-transparent hover:shadow-md hover:shadow-black/10'
                }`}
              >
                {tb.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm shadow-sm">{error}</div>
      )}

      {/* ═══════════════════════ TAB : DEMANDES ═══════════════════════ */}
      {tab === 'requests' && (
        <>
          {/* Status pills */}
          <div className="flex gap-2">
            {([
              { key: 'pending' as Status, label: t('adm_sign_st_pending'), color: 'amber' },
              { key: 'approved' as Status, label: t('adm_sign_st_approved'), color: 'emerald' },
              { key: 'rejected' as Status, label: t('adm_sign_st_rejected'), color: 'rose' },
            ]).map((s) => (
              <button
                key={s.key}
                onClick={() => setStatus(s.key)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 border transform hover:scale-105 active:scale-95 ${
                  status === s.key
                    ? s.color === 'amber'
                      ? 'bg-amber-500 text-white border-amber-500 shadow-lg shadow-amber-500/30 ring-2 ring-amber-300/50'
                      : s.color === 'emerald'
                      ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/30 ring-2 ring-emerald-300/50'
                      : 'bg-rose-500 text-white border-rose-500 shadow-lg shadow-rose-500/30 ring-2 ring-rose-300/50'
                    : 'bg-white text-zce-navy border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-16 text-zce-gray-muted">
              <div className="inline-block w-8 h-8 border-3 border-zce-navy/20 border-t-zce-navy rounded-full animate-spin mb-3" />
              <p className="text-sm">{t('adm_loading')}</p>
            </div>
          ) : list.length === 0 ? (
            <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center text-2xl">
                {status === 'pending' ? '📭' : status === 'approved' ? '✅' : '❌'}
              </div>
              <p className="text-sm text-zce-gray-muted">
                {status === 'pending' ? t('adm_sign_empty_pending') : status === 'approved' ? t('adm_sign_empty_approved') : t('adm_sign_empty_rejected')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {list.map((r) => {
                const fullName = [r.firstName, r.lastName].filter(Boolean).join(' ') || '—';
                const initials = (fullName.match(/\b\w/g) || []).slice(0, 2).join('').toUpperCase();
                return (
                  <div key={r.id} className="bg-white rounded-xl shadow-zce border border-gray-100 p-4 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-zce-navy to-zce-navy-light flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-sm">
                        {initials || '?'}
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-zce-navy truncate">{r.organizationName}</p>
                          <span className="text-[10px] font-mono text-zce-gray-muted bg-gray-100 px-1.5 py-0.5 rounded">
                            {r.organizationSlug}
                          </span>
                        </div>
                        <p className="text-xs text-zce-gray-muted truncate mt-0.5">
                          {fullName} · {r.email}
                        </p>
                        {r.message && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1 italic">{t('adm_quoted', { text: r.message })}</p>
                        )}
                      </div>
                      {/* Date */}
                      <div className="text-xs text-zce-gray-muted text-right shrink-0 hidden sm:block">
                        {new Date(r.createdAt).toLocaleDateString(locale)}
                      </div>
                      {/* Actions */}
                      <div className="flex gap-2 shrink-0">
                        {status === 'pending' ? (
                          <>
                            <button
                              onClick={() => approve(r)}
                              className="px-3.5 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 rounded-lg transition-all duration-200 shadow-md shadow-emerald-500/25 hover:shadow-lg hover:shadow-emerald-500/40 transform hover:scale-105 active:scale-95"
                            >
                              ✓ {t('adm_sign_approve')}
                            </button>
                            <button
                              onClick={() => reject(r)}
                              className="px-3.5 py-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 hover:border-rose-300 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:shadow-rose-500/15 transform hover:scale-105 active:scale-95"
                            >
                              ✕ {t('adm_sign_reject')}
                            </button>
                          </>
                        ) : (
                          <span className="text-[11px] text-zce-gray-muted">
                            {r.reviewedAt ? new Date(r.reviewedAt).toLocaleString(locale) : '—'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════ TAB : ORGANISATIONS ═══════════════════════ */}
      {tab === 'organizations' && (
        <>
          {loading ? (
            <div className="text-center py-16 text-zce-gray-muted">
              <div className="inline-block w-8 h-8 border-3 border-zce-navy/20 border-t-zce-navy rounded-full animate-spin mb-3" />
              <p className="text-sm">{t('adm_loading')}</p>
            </div>
          ) : orgs.length === 0 ? (
            <div className="bg-white rounded-xl shadow-zce border border-gray-100 p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center text-2xl">🏢</div>
              <p className="text-sm text-zce-gray-muted">{t('adm_sign_no_orgs')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orgs.map((o) => (
                <div key={o.id} className="bg-white rounded-xl shadow-zce border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                  {/* Org header */}
                  <div className="px-5 py-4 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white text-lg font-bold shrink-0 shadow-sm">
                      {o.name[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-bold text-zce-navy truncate">{o.name}</h3>
                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                          o.status === 'active'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {o.status === 'active' ? t('adm_cli_st_active') : o.status}
                        </span>
                        {o.plan && (
                          <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-50 text-blue-600">
                            {o.plan}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-zce-gray-muted mt-0.5">
                        <span className="font-mono">{o.slug}</span> · {t('adm_sign_created_on', { date: new Date(o.createdAt).toLocaleDateString(locale) })}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                        {tp('adm_sign_members', o.members.length)}
                      </span>
                      <button
                        onClick={() => deleteOrg(o)}
                        className="px-3.5 py-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 hover:border-rose-300 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:shadow-rose-500/15 transform hover:scale-110 active:scale-95"
                        title={t('adm_sign_delete_org_title')}
                        aria-label={t('adm_sign_delete_org_title')}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  {/* Members compact list */}
                  {o.members.length > 0 && (
                    <div className="border-t border-gray-100 bg-gray-50/50 divide-y divide-gray-100">
                      {o.members.map((m) => {
                        const mName = [m.firstName, m.lastName].filter(Boolean).join(' ') || '—';
                        return (
                          <div key={`${m.orgId}-${m.userId}`} className="px-5 py-2.5 flex items-center gap-3">
                            <div className="w-7 h-7 rounded-full bg-zce-navy/10 flex items-center justify-center text-[10px] font-bold text-zce-navy shrink-0">
                              {(m.firstName?.[0] || m.userEmail[0]).toUpperCase()}
                            </div>
                            <span className="text-xs font-medium text-zce-navy truncate flex-1 min-w-0">{mName}</span>
                            <span className="text-[11px] text-zce-gray-muted truncate hidden sm:inline">{m.userEmail}</span>
                            <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium shrink-0 ${ORG_ROLE_BADGE[m.orgRole]}`}>
                              {t(ORG_ROLE_LABEL[m.orgRole])}
                            </span>
                            <span className="text-[10px] text-zce-gray-muted shrink-0 hidden md:inline capitalize">
                              {PLATFORM_ROLE_LABEL[m.platformRole] ? t(PLATFORM_ROLE_LABEL[m.platformRole]) : m.platformRole.replace('_', ' ')}
                            </span>
                            <span className="text-[10px] text-zce-gray-muted shrink-0 hidden lg:inline">
                              {new Date(m.joinedAt).toLocaleDateString(locale)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {pendingDelete && (
        <ConfirmDeleteOrgModal
          orgName={pendingDelete.name}
          memberCount={pendingDelete.memberCount}
          onCancel={() => setPendingDelete(null)}
          onConfirm={confirmDeleteOrg}
        />
      )}
    </div>
  );
}

// ─── Confirm Delete Organization Modal (name typing + password re-auth) ───
function ConfirmDeleteOrgModal({
  orgName,
  memberCount,
  onCancel,
  onConfirm,
}: {
  orgName: string;
  memberCount: number;
  onCancel: () => void;
  onConfirm: (password: string) => Promise<void>;
}) {
  const [typed, setTyped] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t, tp } = useI18n();

  const nameOk = typed.trim() === orgName;
  const canSubmit = nameOk && password.length > 0 && !submitting;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(password);
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white text-slate-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden ring-1 ring-slate-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-br from-red-50 to-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 className="w-5 h-5">
              <path d="M3 6h18 M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2 M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6 M10 11v6 M14 11v6" />
            </svg>
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-slate-900">{t('adm_cli_delete_title')}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{t('adm_inst_c_delete_sub')}</p>
          </div>
          <button onClick={onCancel} aria-label={t('adm_close')}
                  className="text-slate-400 hover:text-slate-600 transition rounded p-1">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 className="w-5 h-5"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          <div className="flex gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 className="w-5 h-5 shrink-0 mt-0.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z M12 9v4 M12 17h.01" />
            </svg>
            <div>
              {t('adm_sign_del_text_1')} <strong className="font-semibold">{t('adm_quoted', { text: orgName })}</strong> {t('adm_sign_del_text_2')}{' '}
              <strong>{tp('adm_sign_members', memberCount)}</strong>{' '}
              {t('adm_sign_del_text_3')}
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4" id="confirm-delete-org-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('adm_sign_type_name')}
              </label>
              <input
                type="text" autoFocus value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className={`w-full px-3 py-2.5 bg-white border rounded-lg text-sm text-slate-900 placeholder-slate-400
                           focus:outline-none focus:ring-2 ${
                             typed.length === 0
                               ? 'border-slate-300 focus:ring-primary-500 focus:border-primary-500'
                               : nameOk
                               ? 'border-emerald-400 focus:ring-emerald-500 focus:border-emerald-500'
                               : 'border-red-300 focus:ring-red-500 focus:border-red-500'
                           }`}
                placeholder={orgName}
              />
              <p className="mt-1 text-xs text-slate-500">
                {t('adm_sign_type_exact_1')} <span className="font-mono font-medium text-slate-700">{orgName}</span> {t('adm_sign_type_exact_2')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {t('adm_sign_password')}
              </label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 bg-white border border-slate-300 rounded-lg text-sm
                             text-slate-900 placeholder-slate-400
                             focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('adm_sign_password_ph')}
                />
                <button type="button" tabIndex={-1} onClick={() => setShowPwd(s => !s)}
                        aria-label={showPwd ? t('adm_sign_hide_pwd') : t('adm_sign_show_pwd')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600">
                  {showPwd ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                         className="w-4 h-4">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                         className="w-4 h-4">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                     className="w-4 h-4 shrink-0 mt-0.5">
                  <circle cx="12" cy="12" r="10" /><path d="M12 8v4 M12 16h.01" />
                </svg>
                <span>{error}</span>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={submitting}
                  className="px-4 py-2.5 text-sm font-semibold rounded-lg border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 transition-all duration-200 disabled:opacity-50 transform hover:scale-105 active:scale-95 hover:shadow-md">
            {t('adm_cancel')}
          </button>
          <button type="submit" form="confirm-delete-org-form" disabled={!canSubmit}
                  className="px-5 py-2.5 text-sm font-bold rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-md shadow-red-500/25 hover:shadow-lg hover:shadow-red-500/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none inline-flex items-center gap-2 transform hover:scale-105 active:scale-95">
            {submitting && (
              <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {submitting ? t('adm_sign_deleting') : t('adm_inst_delete_forever')}
          </button>
        </div>
      </div>
    </div>
  );
}
